import { onMounted, onUnmounted, onUpdated } from 'vue';
import { useMediaQuery } from '@vueuse/core';
import { MARKDOWN_WRAPPER_CLASS } from '~/utilities/constants';

const MARKDOWN_WRAPPER_SELECTOR = `.${MARKDOWN_WRAPPER_CLASS}`;
const OUTLINE_LINK_SELECTOR = '.toc-outline-link';
const OUTLINE_LINK_ACTIVE_CLASS = '!opacity-100';
const HEADER_ANCHOR_SELECTOR = `${MARKDOWN_WRAPPER_SELECTOR} .header-anchor`;

export interface HeaderWithChildren {
  /** The text of the header. */
  text: string;
  /** Link of the header. Typically using `#${slug}` as the anchor hash. */
  link: string;
  /** The children of the header. */
  children: HeaderWithChildren[];
}

export function getHeaders() {
  const target = MARKDOWN_WRAPPER_SELECTOR;
  const matches = document.querySelectorAll(`${target} h2, ${target} h3`);
  const removePoundSymbol = (s: string) => s.trim().replaceAll('#', '');
  const headers: HeaderWithChildren[] = [];

  matches.forEach((match) => {
    if (match.tagName === 'H2' && match instanceof HTMLElement) {
      headers.push({
        text: removePoundSymbol(match.innerText),
        link: `#${match.id}`,
        children: [],
      });
    }
    if (match.tagName === 'H3' && match instanceof HTMLElement) {
      headers[headers.length - 1]?.children.push({
        text: removePoundSymbol(match.innerText),
        link: `#${match.id}`,
        children: [],
      });
    }
  });

  return headers;
}

export function useActiveAnchor(
  container: HTMLElement, // E.g. The element that contains the outline links.
  marker: HTMLElement, // E.g. The element that will be used to highlight the active link.
) {
  // Disable the outline when the screen is too small.
  const isOutlineEnabled = useMediaQuery('(min-width: 1280px)');
  const onScroll = throttleAndDebounce(setActiveLink, 100);
  let prevActiveLink: HTMLAnchorElement | null = null;

  onMounted(() => {
    requestAnimationFrame(setActiveLink);
    window.addEventListener('scroll', onScroll);
  });

  onUpdated(() => {
    activateLink(location.hash);
  });

  onUnmounted(() => {
    window.addEventListener('scroll', onScroll);
  });

  function setActiveLink(): void {
    if (!isOutlineEnabled.value) {
      return;
    }

    // Get all the links from the outline.
    const links = [].slice.call(
      container.querySelectorAll(OUTLINE_LINK_SELECTOR),
    ) as HTMLAnchorElement[];

    // Get all the headers' anchors on the page.
    const anchors = [].slice
      .call(document.querySelectorAll(HEADER_ANCHOR_SELECTOR))
      .filter((anchor: HTMLAnchorElement) =>
        links.some((link) => link.hash === anchor.hash && anchor.offsetParent !== null),
      ) as HTMLAnchorElement[];

    // page bottom - highlight last one
    const isBottom = window.scrollY + window.innerHeight >= document.body.offsetHeight - 1;

    // Page bottom - highlight last item.
    if (anchors.length && isBottom) {
      activateLink(anchors[anchors.length - 1].hash);
      return;
    }

    for (let i = 0; i < anchors.length; i++) {
      const anchor = anchors[i];
      const nextAnchor = anchors[i + 1];

      const [isActive, hash] = isAnchorActive(i, anchor, nextAnchor);

      if (isActive) {
        activateLink(hash);
        return;
      }
    }
  }

  function activateLink(hash: string | null): void {
    if (prevActiveLink) {
      prevActiveLink.classList.remove(OUTLINE_LINK_ACTIVE_CLASS);
    }

    const activeLink = (prevActiveLink =
      hash == null
        ? null
        : (container.querySelector(`a[href="${decodeURIComponent(hash)}"]`) as HTMLAnchorElement));
    if (activeLink) {
      activeLink.classList.add(OUTLINE_LINK_ACTIVE_CLASS);
      marker.style.opacity = '1';
      marker.style.top = `${activeLink.offsetTop + 32}px`;
    } else {
      marker.style.opacity = '0';
      marker.style.top = '28px';
    }
  }
}

function getAnchorTop(anchor: HTMLAnchorElement): number {
  const headingEl = anchor.parentElement as HTMLElement;
  // This number can be adjusted depending on when we want the marker to appear.
  return headingEl.offsetTop - 150;
}

function isAnchorActive(
  index: number,
  anchor: HTMLAnchorElement,
  nextAnchor: HTMLAnchorElement | undefined,
): [boolean, string | null] {
  const scrollTop = window.scrollY;

  // Page top - don't hightlight anything.
  if (index === 0 && scrollTop === 0) {
    return [true, null];
  }

  // Anchor is not active.
  if (scrollTop < getAnchorTop(anchor)) {
    return [false, null];
  }

  // Anchor is active.
  if (!nextAnchor || scrollTop < getAnchorTop(nextAnchor)) {
    return [true, anchor.hash];
  }

  // Anchor is not active.
  return [false, null];
}

function throttleAndDebounce(fn: () => void, delay: number): () => void {
  let timeout: any;
  let called = false;

  return () => {
    if (timeout) {
      clearTimeout(timeout);
    }

    if (!called) {
      fn();
      called = true;
      setTimeout(() => {
        called = false;
      }, delay);
    } else {
      timeout = setTimeout(fn, delay);
    }
  };
}
